/**
 * RelatedOptions container renders the `More Help Options` section
 */
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';

import l10n from '../../lib/reactPPl10nHelper';
import HelpOption from '../components/common/HelpOption';
import { trackLink } from '../../lib/analytics-helper';
import Page from '../../lib/page';
import { loadModalContainer } from '../actions/action-login-modal';
import { locationUpdate } from '../actions/action-contact-location';
import { chatBotContactTo } from '../constants/UtilVariables';
import { channelMap, callUsChannelMapping } from '../../common/commonConstants';
import { CONTACT_OPTION } from '../constants/PropTypesShapes';
import {
  getC3MarketChannelEligibility,
  isC2MerchantMigrated,
  getC3MerchantMigrationConfig,
  isDeployedInGoPay,
} from '../../lib/globalHelper';

import i18n from '../../lib/reacti18n';

const label = {
  header: i18n.message('smarthelp/callUs.call.additionOptions.header'),
};
// add a display function with key
const isLoggedIn = Page.isLoggedIn();

const RelatedOptions = function (props) {
  const { communityCount, contactChannels, manageNumber, managerEmail } = props;

  const linkValidation = (link) => typeof link === 'string' && link.length;

  // Checking for channel option
  const enableContactChannel = (channelName) =>
    !!contactChannels.find(
      (channel) => channel.name === channelName || (channel.type && channel.type.toUpperCase() === channelName),
    );

  // Help options attribute filter
  const getOptionsDetail = () => [
    {
      key: callUsChannelMapping.HELP_CENTER,
      display: true,
      className: callUsChannelMapping.HELP_CENTER,
      header: l10n.getContent('smarthelp/callUs.call.additionOptions.new.help.header'),
      message: l10n.getContent('smarthelp/callUs.call.additionOptions.help.label'),
      onClick: () => {
        trackLink({ id: 'callPage', link: 'help-options|helpCenter' });
        window.open(Page.getHCRHomeURL(), '_self');
      },
    },
    {
      key: callUsChannelMapping.COMMUNITY,
      // if market is enabled in c3merchantMigration remote config and community should be in the channels of same RC
      // or c2merchantMigrated flag should be true and community should be in the channels of same RC(c3merchantMigration RC)
      // if market is not enabled in c3merchantMigration remote config and c2merchantflag is false just display based on old conditions
      display:
        linkValidation(Page.getCommunityLink()) &&
        enableContactChannel(channelMap.community) &&
        getC3MarketChannelEligibility(channelMap.community),
      className: callUsChannelMapping.COMMUNITY,
      header: l10n.getContent('smarthelp/contactOption.community.header'),
      message: l10n.getContent('smarthelp/contactOption.community.online', { onlineMember: communityCount }),
      onClick: () => {
        trackLink({ id: 'callPage', link: 'help-options|community' });
        window.open(Page.getCommunityLink());
      },
    },
    {
      key: callUsChannelMapping.EMAIL,
      // hiding email when AM data is present
      // if market is enabled in c3merchantMigration remote config and emailus should be in the channels of same RC
      // or c2merchantMigrated flag should be true and emailus should be in the channels of same RC(c3merchantMigration RC)
      // if market is not enabled in c3merchantMigration remote config and c2merchantflag is false just display based on old conditions
      display:
        enableContactChannel(channelMap.email) &&
        !manageNumber &&
        !managerEmail &&
        getC3MarketChannelEligibility(channelMap.email),
      className: callUsChannelMapping.EMAIL,
      header: l10n.getContent('smarthelp/contactOption.emailUs.header'),
      message: l10n.getContent('smarthelp/callUs.call.additionOptions.email.label'),
      onClick: () => {
        trackLink({ id: 'callPage', link: 'help-options|emailUs' });
        const c3MerchantMigrationConfig = getC3MerchantMigrationConfig();
        if (isDeployedInGoPay() && c3MerchantMigrationConfig.goPayPhase2Enabled) {
          return window.open(c3MerchantMigrationConfig.goPayPhase2Url, '_self');
        }
        if (isC2MerchantMigrated()) {
          if (c3MerchantMigrationConfig.goPayEnabled) {
            return window.open(`${c3MerchantMigrationConfig.goPayEnabledUrl}`, '_self');
          }
          return window.open(`mailto:${c3MerchantMigrationConfig.emailUs}`);
        }

        if (!isLoggedIn) {
          return props.loadModalContainer('email');
        }
        if (Page.isRedirectToSMC()) {
          return Page.redirectToSMC();
        }
        if (Page.getChatBotContactTo() === chatBotContactTo.call) {
          return window.open('/smarthelp/contact-us?contact=email', '_self');
        }
        Page.scrollToTop();
        return props.locationUpdate({ pageName: 'email' });
      },
    },
    {
      key: callUsChannelMapping.RES_CENTER,
      // if market is enabled in c3merchantMigration remote config and resolution center should be in the channels of same RC
      // or c2merchantMigrated flag should be true and resolution center should be in the channels of same RC(c3merchantMigration RC)
      // if market is not enabled in c3merchantMigration remote config and c2merchantflag is false just display based on old conditions
      display:
        linkValidation(Page.getResolutionURL()) &&
        (enableContactChannel(channelMap.resCenter) || enableContactChannel(channelMap.seller)) &&
        getC3MarketChannelEligibility(channelMap.seller),
      className: callUsChannelMapping.RES_CENTER,
      header: l10n.getContent('smarthelp/contactLinksOption.label.resolution'),
      message: l10n.getContent('smarthelp/contactLinksOption.messageLabel.resolution.issues'),
      onClick: () => {
        trackLink({ id: 'callPage', link: 'help-options|resolution' });
        window.open(Page.getResolutionURL());
      },
    },
  ];

  const renderOptions = () =>
    getOptionsDetail().map((option) => (
      <li key={option.key}>
        <HelpOption {...option} />
      </li>
    ));

  return (
    <div className="channel-new-call-options-container">
      <h2 className="channel-new-options-header">{label.header}</h2>
      <ul>{renderOptions()}</ul>
    </div>
  );
};

RelatedOptions.propTypes = {
  communityCount: PropTypes.number,
  contactChannels: PropTypes.arrayOf(PropTypes.shape(CONTACT_OPTION)),
  manageNumber: PropTypes.string,
  managerEmail: PropTypes.string,
  loadModalContainer: PropTypes.func.isRequired,
  locationUpdate: PropTypes.func.isRequired,
};

RelatedOptions.defaultProps = {
  communityCount: 0,
  contactChannels: [],
  manageNumber: '',
  managerEmail: '',
};

function mapStateToProps(state) {
  return {
    communityCount: state.activeCommunityUserCount.count,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ loadModalContainer, locationUpdate }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(RelatedOptions);
